import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { authFetch, isAuthenticated } from "../../utils/authUtils";
import StarRating from "./StarRating";
import "./StarRating.css";

interface RatingSummary {
    rating_count: number;
    average_rating: number;
}

interface UserReview {
    id: string;
    rating: number;
    content: string;
    privacy: string;
    _previousRating?: number;
}

interface UserRatingUIProps {
    itemId: number;
    itemType: 'song' | 'album';
    onRatingChange?: () => void;
}

const UserRatingUI: React.FC<UserRatingUIProps> = ({ itemId, itemType, onRatingChange }) => {
    const [ratingSummary, setRatingSummary] = useState<RatingSummary | null>(null);
    const [ratingsRefreshCounter, setRatingsRefreshCounter] = useState(0);
    const [userReview, setUserReview] = useState<UserReview | null>(null);
    const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(false);
    const [isSubmittingRating, setIsSubmittingRating] = useState<boolean>(false);
    const location = useLocation();

    // Check if user is logged in
    useEffect(() => {
        setIsUserLoggedIn(isAuthenticated());
    }, []);

    // Listen for custom event when reviews change
    useEffect(() => {
        const refreshRatings = () => {
            setRatingsRefreshCounter(prev => prev + 1);
        };

        // Add event listener for review changes
        window.addEventListener('reviewUpdated', refreshRatings);

        return () => {
            // Clean up event listener
            window.removeEventListener('reviewUpdated', refreshRatings);
        };
    }, []);

    // Get the current user ID
    const getUserData = () => {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) return null;
            return JSON.parse(userData);
        } catch (e) {
            console.error("Error parsing user data:", e);
            return null;
        }
    };

    // Fetch user's own review if they're logged in
    useEffect(() => {
        const fetchUserReview = async () => {
            if (!isUserLoggedIn || !itemId) {
                console.log("User not logged in or itemId is invalid, skipping review fetch");
                return;
            }

            try {
                // Check if we have a valid token before making the request
                const token = localStorage.getItem('token');
                if (!token) {
                    console.log("No auth token found, user may be logged out");
                    setIsUserLoggedIn(false);
                    return;
                }

                // Try a different approach - get all reviews for the item 
                // and filter for the user's review client-side
                console.log(`Fetching all reviews for ${itemType} ID:`, itemId);

                // This endpoint should be more reliable as it's used elsewhere in the app
                const response = await fetch(`/api/reviews/${itemType}/${itemId}`);

                if (!response.ok) {
                    console.error("Error fetching reviews:", response.status);
                    return;
                }

                // Get the current user data
                const userData = getUserData();
                // Try different possible user ID field names
                const userId = userData?.userId || userData?.id || userData?.user_id;

                console.log("Current user data:", userData);
                console.log("Extracted user ID:", userId);

                if (!userId) {
                    console.log("No user ID found in user data");
                    return;
                }

                // Parse the reviews and find the user's review
                const reviews = await response.json();
                console.log("Found", reviews.length, "reviews for this item");

                // Find the user's review in the list - try different possible ID field names
                const userReviewFromList = reviews.find((review: any) =>
                    review.user_id === userId ||
                    review.userId === userId ||
                    review.is_owner === true
                );

                if (userReviewFromList) {
                    console.log("Found user's review:", userReviewFromList);
                    setUserReview(userReviewFromList);
                } else {
                    console.log("User hasn't reviewed this item yet");
                    setUserReview(null);
                }
            } catch (err) {
                console.error("Error fetching user review:", err);
                setUserReview(null);
            }
        };

        fetchUserReview();
    }, [itemId, itemType, isUserLoggedIn, ratingsRefreshCounter]);

    useEffect(() => {
        const fetchRatingSummary = async () => {
            if (!itemId) {
                console.log("Invalid itemId, skipping rating summary fetch");
                return;
            }

            try {
                const response = await fetch(`/api/reviews/${itemType}/${itemId}/rating`);
                if (response.ok) {
                    const data = await response.json();
                    setRatingSummary(data);
                }
            } catch (err) {
                console.error(`Error fetching ${itemType} rating summary:`, err);
                // Don't set an error for this, as it's not critical
            }
        };

        fetchRatingSummary();
    }, [itemId, itemType, ratingsRefreshCounter]);

    const handleQuickRate = async (rating: number) => {
        if (!isUserLoggedIn || isSubmittingRating || !itemId) return;

        console.log("Attempting to rate item with rating:", rating);
        console.log("Current user review state:", userReview);
        console.log(`${itemType} ID being used:`, itemId);

        setIsSubmittingRating(true);

        // Store the previous rating before updating
        const previousRating = userReview ? userReview.rating : 0;

        // Immediately update the UI with the new rating for instant feedback
        const optimisticReview = userReview
            ? { ...userReview, rating, _previousRating: previousRating }
            : { id: 'temp-id', rating, content: '', privacy: 'public', _previousRating: 0 };

        setUserReview(optimisticReview);

        // Also trigger a rating refresh immediately to update UI everywhere
        setRatingsRefreshCounter(prev => prev + 1);

        // Dispatch custom event to notify that a review was updated
        window.dispatchEvent(new CustomEvent('reviewUpdated'));

        // Dispatch an additional event specifically for the reviews list section
        window.dispatchEvent(new CustomEvent('reviewsListUpdated', {
            detail: {
                itemId: itemId,
                itemType: itemType,
                rating: rating
            }
        }));

        try {
            let response;
            const userData = getUserData();
            const userId = userData?.userId || userData?.id || userData?.user_id;

            console.log("User ID for review:", userId);

            if (userReview && userReview.id) {
                // Update existing review
                console.log("Updating existing review with ID:", userReview.id);

                const updateData = {
                    rating: rating,
                    content: userReview.content || " ",
                    privacy: userReview.privacy || "public"
                };

                console.log("Update data being sent:", updateData);

                response = await authFetch(`/api/reviews/${userReview.id}`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(updateData)
                });
            } else {
                // Create new review
                console.log(`Creating new review for ${itemType} ID:`, itemId);

                // Create a formData object that matches the format used in ReviewForm.tsx
                const formData = {
                    rating: rating,
                    content: " ", // Using a text description as content
                    privacy: "public" // Adding privacy field explicitly
                };

                const requestBody = {
                    [`${itemType}_id`]: itemId,
                    ...formData
                };

                // Log the exact request body being sent
                console.log("Sending review creation request with body:", JSON.stringify(requestBody));

                // Same format as used in ReviewForm.tsx
                response = await authFetch(`/api/reviews${itemType === 'album' ? '/album' : ''}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(requestBody)
                });
            }

            console.log("API response status:", response.status);

            if (response.ok) {
                const data = await response.json();
                console.log("Review saved successfully:", data);

                // Update the user review with the newly created one
                setUserReview({
                    id: data.id,
                    rating: data.rating,
                    content: data.content || '',
                    privacy: data.privacy,
                    _previousRating: userReview?.rating
                });

                // Notify parent component if callback provided
                if (onRatingChange) {
                    onRatingChange();
                }

                // Dispatch custom event to notify that a review was added or updated
                window.dispatchEvent(new CustomEvent('reviewUpdated'));
            } else {
                // Log full error details
                const errorText = await response.text();
                console.error("Error response from server:", errorText);

                // If there was an error, revert to the previous state
                if (userReview) {
                    setUserReview(userReview);
                } else {
                    setUserReview(null);
                }

                throw new Error(`API error: ${response.status} - ${errorText}`);
            }
        } catch (error) {
            console.error("Error in handleQuickRate:", error);
            // If we have response details from the error, log them
            if (error instanceof Error) {
                console.error("Error message:", error.message);
            }

            // Restore original state on error
            if (userReview) {
                setUserReview(userReview);
            } else {
                setUserReview(null);
            }
        } finally {
            setIsSubmittingRating(false);
        }
    };

    // Replace the renderStars function with the StarRating component
    const renderStars = (rating: number) => {
        return <StarRating rating={rating} />;
    };

    // Use the StarRating component for interactive stars
    const renderInteractiveStars = () => {
        // If we received userReview, use that rating. Otherwise use 0 (no stars)
        // We need to make sure to convert to a number, as the rating could come in different formats
        const currentRating = userReview ?
            (typeof userReview.rating === 'string' ? parseFloat(userReview.rating) : userReview.rating) : 0;

        return (
            <StarRating
                rating={currentRating}
                interactive={true}
                onRatingChange={handleQuickRate}
            />
        );
    };

    // Update the average rating display immediately when user rates
    useEffect(() => {
        if (!ratingSummary || !userReview) return;

        // Create an optimistic rating summary that includes the user's new rating
        const oldUserRating = userReview._previousRating; // This may be undefined for new reviews
        const newUserRating = userReview.rating;

        // Only do optimistic update if we have previous rating information
        if (oldUserRating !== undefined && oldUserRating !== newUserRating) {
            console.log("Calculating optimistic rating update");
            console.log("Previous rating:", oldUserRating);
            console.log("New rating:", newUserRating);

            const totalRatings = ratingSummary.rating_count;
            const currentSum = ratingSummary.average_rating * totalRatings;

            // If it's a new rating, add it to the total
            if (oldUserRating === 0) {
                const newSum = currentSum + newUserRating;
                const newCount = totalRatings + 1;
                const newAverage = newSum / newCount;

                console.log("Optimistic rating (new review):", newAverage, "count:", newCount);

                // Update with optimistic values
                setRatingSummary({
                    rating_count: newCount,
                    average_rating: newAverage
                });
            }
            // If it's updating an existing rating, adjust the average
            else {
                const adjustedSum = currentSum - oldUserRating + newUserRating;
                const newAverage = adjustedSum / totalRatings;

                console.log("Optimistic rating (update):", newAverage);

                // Update with optimistic values
                setRatingSummary({
                    rating_count: totalRatings,
                    average_rating: newAverage
                });
            }
        }
    }, [userReview]);

    // Function to handle login redirect with return URL
    const handleLoginRedirect = () => {
        localStorage.setItem('returnToUrl', location.pathname + location.search);
    };

    return (
        <div className="song-rating-summary">
            <div className="rating-container">
                <div className="avg-summary">
                    <div className="rating-info">
                        <div className="rating-type">users rated
                            <div className="rating-number-mobile">
                                <strong>{ratingSummary?.average_rating ? ratingSummary.average_rating.toFixed(1) : "-"}</strong>
                                <div className="number-of-ratings">({ratingSummary?.rating_count || 0})</div>
                            </div>
                        </div>

                        <div className="rating-number">
                            <strong>{ratingSummary?.average_rating ? ratingSummary.average_rating.toFixed(1) : "-"}</strong>
                            <div className="number-of-ratings">({ratingSummary?.rating_count || 0})</div>
                        </div>
                        <div className="rating-stars">
                            {ratingSummary?.average_rating ? renderStars(ratingSummary.average_rating) : renderStars(0)}
                        </div>
                    </div>
                </div>

                <div className="user-rating">
                    {isUserLoggedIn ? (
                        <div className="my-rating">
                            <div className="rating-info">
                                <div className="rating-type">my rating
                                    <div className="rating-number-mobile">
                                        <strong>
                                            {userReview && userReview.rating ?
                                                (typeof userReview.rating === 'number'
                                                    ? userReview.rating.toFixed(1)
                                                    : Number(userReview.rating).toFixed(1))
                                                : "-"}
                                        </strong>
                                    </div>
                                </div>
                                <div className="rating-number">
                                    <strong>
                                        {userReview && userReview.rating ?
                                            (typeof userReview.rating === 'number'
                                                ? userReview.rating.toFixed(1)
                                                : Number(userReview.rating).toFixed(1))
                                            : "-"}
                                    </strong>
                                </div>
                                <div className="rating-stars interactive-stars">
                                    {renderInteractiveStars()}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="login-to-rate">
                            <Link to="/login" className="login-button" onClick={handleLoginRedirect}>Log in to rate</Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserRatingUI; 